/**
 * Done dropdown (SPEC §7.4): "✓ N done ▾" collapses done children. Sort per card,
 * remembered per user. Tap the checkbox to uncheck (returns to its original position),
 * swipe a done row left to archive, or "Archive all done".
 */
import { memo, useRef, useState, type ReactNode } from 'react'
import { dateInTz, displayTitle, doneRows, relativeDayLabel, sortDone, type DoneSort } from '@terrazzo/core'
import { store } from '../store/store'
import { useContent, useDerived, useItem, useMySettings, useToday } from '../store/hooks'
import { actions } from '../store/actions'
import { Checkbox } from './Checkbox'
import { navigate, itemPath } from '../lib/router'
import { deviceZone } from '../lib/clock'
import { IconChevronRight } from './Icons'

const SORTS: Array<{ key: DoneSort; label: string }> = [
  { key: 'recent', label: 'Recent' },
  { key: 'original', label: 'Original' },
  { key: 'az', label: 'A–Z' },
]

/** `lead`: what shares its line, on the left (a card's + Add item). */
export const DoneDropdown = memo(function DoneDropdown({ rootId, ids: given, scope, lead }: { rootId: string; ids?: string[]; scope: string; lead?: ReactNode }) {
  const mine = useMySettings()
  const sort = ((mine[`doneSort.${rootId}`] as DoneSort | undefined) ?? mine.doneSort) as DoneSort
  // A–Z depends on titles, which aren't structural: also follow content changes then.
  const content = useContent()
  const ids = useDerived(() => sortDone(store.idx, given ?? doneRows(store.idx, rootId), sort), [rootId, sort, given, sort === 'az' ? content : 0])
  const [open, setOpen] = useState(false)
  if (!ids.length) return lead ? <>{lead}</> : null
  return (
    <div>
      <div className="flex items-center">
        {lead}
        <span className="flex-1" />
        <button type="button" className="lab flex h-8 items-center gap-1 pl-1.5 text-muted hover:text-fg" onClick={() => setOpen(!open)} aria-expanded={open}>
          {ids.length} done {open ? '↑' : '↓'}
        </button>
      </div>
      {open && (
        <div className="pb-1">
          <div className="lab flex items-center gap-1 pb-1 pl-0.5 text-muted">
            {SORTS.map((s) => (
              <button type="button" key={s.key} onClick={() => actions.setMine(`doneSort.${rootId}`, s.key)} className={`px-1.5 py-0.5 ${sort === s.key ? 'bg-fg text-bg' : 'hover:text-fg'}`}>
                {s.label}
              </button>
            ))}
            <span className="flex-1" />
            <button type="button" className="px-1.5 py-0.5 hover:text-fg" onClick={() => actions.archive(ids)}>
              Archive all done
            </button>
          </div>
          {ids.map((id) => (
            <DoneRow key={id} id={id} scope={scope} />
          ))}
        </div>
      )}
    </div>
  )
})

export const DoneRow = memo(function DoneRow({ id, recurringAt, scope }: { id: string; recurringAt?: number; scope?: string }) {
  const it = useItem(id)
  const today = useToday()
  // When something happened, as a day where this phone is (as its "today" is).
  const tz = deviceZone()
  const [dx, setDx] = useState(0)
  const start = useRef<{ x: number; y: number; active: boolean } | null>(null)
  if (!it) return null
  const at = recurringAt ?? it.completedAt
  // A repeating task done for this time says when it comes back.
  const when = it.reopensOn && it.done === true && !recurringAt ? `Back ${relativeDayLabel(it.reopensOn, today)}` : at ? relativeDayLabel(dateInTz(at, tz), today) : ''
  const title = displayTitle(it.title, (x) => store.get(x)?.title) || 'Untitled'
  void scope
  return (
    <div
      className="row relative"
      style={{ transform: dx ? `translateX(${dx}px)` : undefined, transition: dx ? 'none' : 'transform 160ms' }}
      onPointerDown={(e) => {
        if (e.pointerType !== 'touch' || e.clientX < 24) return
        start.current = { x: e.clientX, y: e.clientY, active: false }
      }}
      onPointerMove={(e) => {
        const s = start.current
        if (!s) return
        const mx = e.clientX - s.x
        const my = e.clientY - s.y
        if (!s.active) {
          if (Math.abs(my) > 10 && Math.abs(my) > Math.abs(mx)) {
            start.current = null
            return
          }
          if (mx < -8) s.active = true
        }
        if (s.active) setDx(Math.max(-96, Math.min(0, mx)))
      }}
      onPointerUp={(e) => {
        const s = start.current
        start.current = null
        if (s?.active && e.clientX - s.x < -48 && !recurringAt) actions.archive([id])
        setDx(0)
      }}
      onPointerCancel={() => {
        start.current = null
        setDx(0)
      }}
    >
      {dx < -20 && <div className="pointer-events-none absolute inset-y-0 -right-24 flex w-24 items-center justify-center text-xs text-muted">Archive</div>}
      <Checkbox checked onToggle={() => (recurringAt ? actions.undoCompletion(id, recurringAt) : actions.toggleDone(id))} label={title} guard={`done:${id}:${recurringAt ?? ''}`} />
      <button type="button" className="row-text text-left text-muted line-through decoration-faint" onClick={() => navigate(itemPath(id))}>
        {title}
      </button>
      <span className="lab mt-2 flex-none pr-1 text-faint">{when}</span>
      <button type="button" className="-mr-1 flex h-9 w-9 flex-none items-center justify-center text-faint" onClick={() => navigate(itemPath(id))} aria-label="Open">
        <span className="open-box">
          <IconChevronRight size={15} />
        </span>
      </button>
    </div>
  )
})
